import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  makeStyles,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Menu,
  InputLabel,
} from "@material-ui/core";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import {
  TABLE_FILTER_OPERATORS,
  TABLE_PAGE_SIZE_OPTIONS,
  TABLE_FILTER_PANEL_PROPS,
} from "components/Table/dataGridDefaults";
import GroupedColumnsManagement from "components/Table/GroupedColumnsManagement";
import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { useTranslation } from "react-i18next";
import {
  getOutputTableData,
  getFilterOptions,
  startAsyncDownload,
  getDownloadProgress,
  downloadFile,
} from "../../viewServices/calls/apiCalls";
import { toast } from "react-toastify";
import AuthContext from "../../../../connections/auth/AuthProvider";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { ThreeDots } from "react-loader-spinner";
import GetAppIcon from "@material-ui/icons/GetApp";
import PropTypes from "prop-types";
import { FilterSystem } from "../../sharedComponents/FilterSystem/index.js";
import { ScenarioSelector } from "../../sharedComponents/ScenarioSelector";

const tablePreviewViewStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  controlCard: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(0),
  },
  controlCardBody: {
    padding: 0,
  },
  controlCardContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: "1",
    overflow: "visible",
  },
  addScenarioBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    flex: "0 0 auto",
  },
  selectFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 140,
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  filtersBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: "1",
    overflow: "visible",
  },
  divider: {
    width: "1px",
    backgroundColor: grayColor[4],
    alignSelf: "stretch",
    margin: `${theme.spacing(1)}px 0`,
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  scenarioSelectorContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    zIndex: 3,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    padding: "12px 16px",
    borderRadius: "4px",
    width: "90%",
    maxWidth: 280,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  selectorLabel: {
    color: "#888",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: theme.spacing(1.5),
  },
  selectedScenarioBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${grayColor[8]}`,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  scenarioBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  selectedScenarioText: {
    fontSize: "14px",
    fontWeight: "500",
    color: grayColor[1],
  },
  scenarioSelectorInBar: {
    minWidth: "200px",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  downloadButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  downloadButton: {
    boxShadow: "none",
    borderColor: clientColor,
    color: grayColor[1],
    "&:hover": {
      color: grayColor[1],
      boxShadow: "none",
      backgroundColor: grayColor[11],
      transform: "scale(1.05)",
      transition: "transform 0.1s",
    },
  },
  contentCard: {
    flex: 1,
    minWidth: "400px",
    height: "100%",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(0),
    overflow: "hidden",
    position: "relative",
  },
  contentCardBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    padding: 0,
  },
  tableWrapper: {
    position: "absolute",
    top: "50px",
    left: "16px",
    right: "16px",
    bottom: "16px",
    overflow: "auto",
  },
  tableContainer: {
    flex: 1,
    height: "100%",
    overflow: "auto",
    minHeight: "200px",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    flex: 1,
  },
  grid: {
    border: "none",
    fontSize: "0.8rem",
    "& .rgm-empty-col": {
      backgroundColor: grayColor[11],
      color: grayColor[4],
    },
    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
      outline: "none",
    },
    // neutral-black toolbar buttons (avoid the vestigial template primary)
    "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
      color: "#2b2f33",
    },
  },
  rowCount: {
    fontSize: "0.8rem",
    color: grayColor[1],
    fontVariantNumeric: "tabular-nums",
  },
});

TablePreviewView.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  isScenariosLoading: PropTypes.bool,
  renderViewButtons: PropTypes.func,
};

// DataGrid toolbar: row count + quick search + column/filter controls + an
// icon-only Export button whose menu reuses the view's async full/summarized
// downloads. Matches the input variant preview toolbar.
function OutputToolbar({
  countText,
  onDownloadFull,
  onDownloadSummarized,
  isDownloading,
  countClass,
  fullLabel,
  summarizedLabel,
  exportLabel,
  searchLabel,
  spinnerColor,
}) {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  return (
    <GridToolbarContainer style={{ padding: "6px 8px", gap: 8 }}>
      <Typography className={countClass}>{countText}</Typography>
      <Box style={{ flex: 1 }} />
      <GridToolbarQuickFilter
        placeholder={searchLabel}
        debounceMs={400}
        variant="outlined"
        size="small"
      />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Tooltip title={exportLabel}>
        <span>
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            disabled={isDownloading}
            aria-label={exportLabel}
            style={{ color: "#2b2f33" }}
          >
            {isDownloading ? (
              <CircularProgress size={16} style={{ color: spinnerColor }} />
            ) : (
              <GetAppIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem
          onClick={() => {
            close();
            onDownloadFull();
          }}
        >
          {fullLabel}
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onDownloadSummarized();
          }}
        >
          {summarizedLabel}
        </MenuItem>
      </Menu>
    </GridToolbarContainer>
  );
}
OutputToolbar.propTypes = {
  countText: PropTypes.string,
  onDownloadFull: PropTypes.func,
  onDownloadSummarized: PropTypes.func,
  isDownloading: PropTypes.bool,
  countClass: PropTypes.string,
  fullLabel: PropTypes.string,
  summarizedLabel: PropTypes.string,
  exportLabel: PropTypes.string,
  searchLabel: PropTypes.string,
  spinnerColor: PropTypes.string,
};

const isEmptyCell = (v) => v === null || v === undefined || v === "";

export default function TablePreviewView({
  scenarios,
  folders = [],
  isScenariosLoading = false,
  renderViewButtons,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    tablePreviewViewStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Independent state for this sub-view
  const [selectedScenario, setSelectedScenario] = useState("");

  // STANDARD 5 filter dimensions
  const [selectedFilters, setSelectedFilters] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });

  // Special parameter - separate from FilterSystem
  const [selectedSolverStatus, setSelectedSolverStatus] = useState("");
  const [solverStatusOptions, setSolverStatusOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [columnGroups, setColumnGroups] = useState(null); // {column_to_group, group_order}
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Cache refs
  const cachedFilterOptions = useRef({});
  const cachedTableData = useRef({});
  const previousScenario = useRef(null);

  // STANDARD 5 filter dimensions only
  const filterDimensions = [
    { key: "products", label: t("overview.filter_dimensions.products") },
    { key: "brands", label: t("overview.filter_dimensions.brands") },
    { key: "channels", label: t("overview.filter_dimensions.channels") },
    { key: "regions", label: t("overview.filter_dimensions.regions") },
    { key: "clients", label: t("overview.filter_dimensions.clients") },
  ];

  // Load filter options when scenario changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!selectedScenario) {
        setFilterOptions({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setSolverStatusOptions([]);
        return;
      }

      setFiltersLoading(true);
      try {
        const cacheKey = selectedScenario;
        let filterData;

        if (cachedFilterOptions.current[cacheKey]) {
          filterData = cachedFilterOptions.current[cacheKey];
        } else {
          filterData = await getFilterOptions(selectedScenario);
          cachedFilterOptions.current[cacheKey] = filterData;
        }

        // Standard 5 filters
        setFilterOptions({
          products: filterData.products || [],
          brands: filterData.brands || [],
          channels: filterData.channels || [],
          regions: filterData.regions || [],
          clients: filterData.clients || [],
        });
        // Special parameter
        setSolverStatusOptions(filterData.solver_status || []);

        // Reset filters only if scenario actually changed
        if (
          previousScenario.current !== selectedScenario &&
          previousScenario.current !== null
        ) {
          setSelectedFilters({
            products: [],
            brands: [],
            channels: [],
            regions: [],
            clients: [],
          });
          setSelectedSolverStatus("");
        }

        previousScenario.current = selectedScenario;
      } catch (err) {
        console.error("Failed to fetch filter options:", err);
        toast.error(t("toast.filter_options_error"));
      } finally {
        setFiltersLoading(false);
      }
    };

    fetchFilterOptions();
  }, [selectedScenario, t]);

  // Load table data when scenario changes
  useEffect(() => {
    const fetchTableData = async () => {
      if (!selectedScenario) {
        setTableData([]);
        setTableHeaders([]);
        setColumnGroups(null);
        return;
      }

      const cacheKey = `${selectedScenario}-no-filters`;
      if (cachedTableData.current[cacheKey]) {
        const cached = cachedTableData.current[cacheKey];
        setTableData(cached.data);
        setTableHeaders(cached.headers);
        setColumnGroups(cached.column_groups || null);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const outputData = await getOutputTableData(
          selectedScenario,
          false,
          false,
          {},
        );
        setTableData(outputData.data);
        setTableHeaders(outputData.headers);
        setColumnGroups(outputData.column_groups || null);
        cachedTableData.current[cacheKey] = outputData;
      } catch (err) {
        console.error("Failed to fetch table data:", err);
        setError(err);
        toast.error(t("toast.load_error"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTableData();
  }, [selectedScenario, t]);

  const handleFiltersChange = useCallback(
    async (newFilters) => {
      setSelectedFilters(newFilters);

      if (selectedScenario) {
        setIsLoading(true);
        setError(null);
        try {
          // Combine standard filters with special parameter
          const allFilters = {
            ...newFilters,
          };

          // Only add solver_status if it has a value
          if (selectedSolverStatus && selectedSolverStatus !== "") {
            allFilters.solver_status = selectedSolverStatus;
          }

          const outputData = await getOutputTableData(
            selectedScenario,
            false,
            false,
            allFilters,
          );

          setTableData(outputData.data);
          setTableHeaders(outputData.headers);
          setColumnGroups(outputData.column_groups || null);
        } catch (err) {
          console.error("Failed to fetch filtered table data:", err);
          setError(err);
          toast.error(t("toast.load_error"));
        } finally {
          setIsLoading(false);
        }
      }
    },
    [selectedScenario, selectedSolverStatus, t],
  );

  // Reload data when solver status changes
  useEffect(() => {
    if (selectedScenario) {
      handleFiltersChange(selectedFilters);
    }
  }, [selectedSolverStatus]); // Only trigger on solver status change

  const handleDownload = async (fullTable) => {
    setIsDownloading(true);
    try {
      const downloadJob = await startAsyncDownload(
        selectedScenario,
        fullTable,
        selectedFilters,
      );
      const downloadId = downloadJob.download_id;
      const progressToastId = `download-progress-${downloadId}`;

      toast.info(
        <div>
          <div style={{ marginBottom: "8px" }}>
            {t("overview.download.starting")}
          </div>
          <div
            style={{
              width: "100%",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              height: "8px",
            }}
          >
            <div
              style={{
                width: "0%",
                backgroundColor: clientColor,
                height: "100%",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "0.8em",
              marginTop: "4px",
              color: "#666",
            }}
          >
            {t("overview.download.progress", { progress: 0 })}
          </div>
        </div>,
        {
          toastId: progressToastId,
          autoClose: false,
          closeButton: false,
          position: "top-right",
        },
      );

      const pollInterval = setInterval(async () => {
        try {
          const progress = await getDownloadProgress(downloadId);

          toast.update(progressToastId, {
            render: (
              <div>
                <div style={{ marginBottom: "8px" }}>{progress.stage}</div>
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "8px",
                  }}
                >
                  <div
                    style={{
                      width: `${progress.progress}%`,
                      backgroundColor: clientColor,
                      height: "100%",
                      borderRadius: "4px",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "0.8em",
                    marginTop: "4px",
                    color: "#666",
                  }}
                >
                  {t("overview.download.progress", {
                    progress: Math.round(progress.progress),
                  })}
                </div>
              </div>
            ),
            type: "info",
            autoClose: false,
            closeButton: false,
          });

          if (progress.status === "success" && progress.file_ready) {
            clearInterval(pollInterval);
            toast.update(progressToastId, {
              render: (
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    {t("overview.download.ready")}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#4caf50",
                        height: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    {t("overview.download.complete")}
                  </div>
                </div>
              ),
              type: "success",
              autoClose: false,
              closeButton: true,
              closeOnClick: true,
            });

            try {
              const fileBlob = await downloadFile(downloadId);
              const url = URL.createObjectURL(fileBlob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `output_scenario_${selectedScenario}_${fullTable ? "full" : "summarized"}.xlsx`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (fileErr) {
              console.error("Error downloading file:", fileErr);
              toast.error(t("overview.download.file_download_failed"));
            }
            setIsDownloading(false);
          } else if (progress.status === "error") {
            clearInterval(pollInterval);
            toast.update(progressToastId, {
              render: (
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    {t("overview.download.failed", {
                      error: progress.error_message,
                    })}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#f44336",
                        height: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    {t("overview.download.error_occurred")}
                  </div>
                </div>
              ),
              type: "error",
              autoClose: false,
              closeButton: true,
              closeOnClick: true,
            });
            setIsDownloading(false);
          }
        } catch (pollError) {
          console.error("Error polling download progress:", pollError);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(pollInterval);
        if (isDownloading) {
          setIsDownloading(false);
          toast.error(t("overview.download.timeout"));
        }
      }, 600000);
    } catch (err) {
      console.error("Failed to start download:", err);
      toast.error(t("overview.download.start_failed"));
      setIsDownloading(false);
    }
  };

  // Build DataGrid columns/rows from the headers + row-arrays; all-empty
  // columns are greyed and pushed to the end. Columns sharing a group
  // (column_to_group from the backend) are clustered together and get a group
  // header via columnGroupingModel; the backend's catch-all "General" bucket
  // is treated as ungrouped (no header).
  const { gridColumns, gridRows, columnGroupsMap } = useMemo(() => {
    if (!tableHeaders || tableHeaders.length === 0) {
      return { gridColumns: [], gridRows: [], columnGroupsMap: {} };
    }
    const emptyOf = (i) => tableData.every((row) => isEmptyCell(row[i]));
    const colToGroup = columnGroups?.column_to_group || {};
    const groupOf = (i) => {
      const g = colToGroup[i];
      return g && g !== "General" ? g : null;
    };

    // Cluster grouped columns at the first member's position; ungrouped in
    // place. Keeps each group contiguous so its header spans one run.
    const clusterByGroup = (list) => {
      const out = [];
      const emitted = new Set();
      list.forEach((i) => {
        if (emitted.has(i)) return;
        const g = groupOf(i);
        if (g) {
          list.forEach((j) => {
            if (!emitted.has(j) && groupOf(j) === g) {
              out.push(j);
              emitted.add(j);
            }
          });
        } else {
          out.push(i);
          emitted.add(i);
        }
      });
      return out;
    };

    const idx = tableHeaders.map((_, i) => i);
    const nonEmpty = clusterByGroup(idx.filter((i) => !emptyOf(i)));
    const empty = clusterByGroup(idx.filter(emptyOf));
    const ordered = [...nonEmpty, ...empty];

    const gridColumns = ordered.map((i) => ({
      field: `c${i}`,
      headerName: tableHeaders[i],
      flex: 1,
      minWidth: 130,
      filterOperators: TABLE_FILTER_OPERATORS,
      headerClassName: emptyOf(i) ? "rgm-empty-col" : undefined,
      cellClassName: emptyOf(i) ? "rgm-empty-col" : undefined,
    }));
    const gridRows = tableData.map((row, r) => {
      const obj = { id: r };
      tableHeaders.forEach((_, i) => {
        obj[`c${i}`] = row[i];
      });
      return obj;
    });

    // {field: group} map for the grouped columns panel — the grouping lives
    // in the columns selector (GroupedColumnsManagement) now, not as an extra
    // header row on the grid.
    const groupsMap = {};
    ordered.forEach((i) => {
      const g = groupOf(i);
      if (g) groupsMap[`c${i}`] = g;
    });

    return { gridColumns, gridRows, columnGroupsMap: groupsMap };
  }, [tableHeaders, tableData, columnGroups]);

  return (
    <Box className={classes.container}>
      {/* Control Bar - Filters + View Buttons only */}
      <Card className={classes.controlCard}>
        <CardBody className={classes.controlCardBody}>
          <Box className={classes.controlCardContent}>
            {/* Left Section: Volume + Solver Status + Filters */}
            <Box className={classes.leftSection}>
              {/* Volume Selector */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel id="volume-selector-label-tablepreview">
                    {t("overview.volume_weight_label")}
                  </InputLabel>
                  <Select
                    labelId="volume-selector-label-tablepreview"
                    value="volume_sellthrough"
                    label={t("overview.volume_weight_label")}
                    disabled={!selectedScenario || filtersLoading}
                  >
                    <MenuItem value="volume_sellthrough">
                      {t("overview.volume_options.volume_sellthrough")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Solver Status Dropdown */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel
                    id="solver-status-selector-label-tablepreview"
                    shrink
                  >
                    {t("overview.solver_status_label")}
                  </InputLabel>
                  <Select
                    labelId="solver-status-selector-label-tablepreview"
                    value={selectedSolverStatus}
                    onChange={(e) => setSelectedSolverStatus(e.target.value)}
                    label={t("overview.solver_status_label")}
                    displayEmpty
                    disabled={!selectedScenario || filtersLoading}
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <em>{t("overview.all_statuses")}</em>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("overview.all_statuses")}</em>
                    </MenuItem>
                    {solverStatusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box className={classes.divider} />

              {/* FilterSystem - Standard 5 dimensions */}
              <Box className={classes.filtersBox}>
                <FilterSystem
                  dimensions={filterDimensions}
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  onFiltersChange={handleFiltersChange}
                  clientColor={clientColor}
                  disabled={!selectedScenario || filtersLoading}
                />
              </Box>
            </Box>

            {/* Right Section: View Buttons only */}
            <Box className={classes.rightSection}>
              {renderViewButtons && renderViewButtons()}
            </Box>
          </Box>
        </CardBody>
      </Card>

      {/* Content Card with Scenario Selector Overlay and Scenario Bar */}
      <Card className={classes.contentCard}>
        <CardBody className={classes.contentCardBody}>
          {/* Scenario Selector Overlay - show when no scenario selected */}
          {!selectedScenario && (
            <Box className={classes.scenarioSelectorContainer}>
              <Box className={classes.selectorLabel}>
                {t("map.tooltip.select_scenario")}
              </Box>
              <ScenarioSelector
                scenarios={scenarios}
                folders={folders}
                value={selectedScenario}
                onChange={setSelectedScenario}
                placeholder={t("overview.choose_scenario")}
                clientColor={clientColor}
                isLoading={isScenariosLoading}
                fullWidth
              />
            </Box>
          )}

          {/* Selected Scenario Bar with download buttons - appears when scenario selected */}
          {selectedScenario && (
            <Box className={classes.selectedScenarioBar}>
              <Box className={classes.scenarioBarLeft}>
                <Box
                  className={classes.selectedScenarioText}
                  style={{ marginRight: "8px" }}
                >
                  {t("overview.select_scenario")}
                </Box>
                <ScenarioSelector
                  scenarios={scenarios}
                  folders={folders}
                  value={selectedScenario}
                  onChange={setSelectedScenario}
                  clientColor={clientColor}
                  isLoading={isScenariosLoading}
                />
              </Box>
            </Box>
          )}

          {/* Table Content Wrapper - separate from scenario bar */}
          <Box className={classes.tableWrapper}>
            {isLoading ? (
              <Box className={classes.loadingBox}>
                <ThreeDots
                  height="80"
                  width="80"
                  radius="9"
                  color={clientColor}
                  visible={true}
                />
              </Box>
            ) : error ? (
              <Typography color="error">
                {t("error")}: {error.message}
              </Typography>
            ) : selectedScenario ? (
              <DataGrid
                className={classes.grid}
                rows={gridRows}
                columns={gridColumns}
                density="compact"
                disableRowSelectionOnClick
                showToolbar
                slots={{
                  toolbar: OutputToolbar,
                  // grouped columns selector (replaces the group header row)
                  columnsManagement: GroupedColumnsManagement,
                }}
                slotProps={{
                  columnsManagement: {
                    groups: columnGroupsMap,
                    searchPlaceholder: t("overview.search"),
                    clientColor,
                  },
                  filterPanel: TABLE_FILTER_PANEL_PROPS,
                  toolbar: {
                    countText: t("overview.rows_count", {
                      count: gridRows.length.toLocaleString(),
                    }),
                    onDownloadFull: () => handleDownload(true),
                    onDownloadSummarized: () => handleDownload(false),
                    isDownloading,
                    countClass: classes.rowCount,
                    fullLabel: t("overview.download_full_table"),
                    summarizedLabel: t("overview.download_summarized_table"),
                    exportLabel: t("overview.export"),
                    searchLabel: t("overview.search"),
                    spinnerColor: clientColor,
                  },
                }}
                pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25, page: 0 } },
                }}
              />
            ) : null}
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
}
